import { useState, useEffect } from "react";
import { Plus, Trash, Tag, Palette, FileText, Check, Eye } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";

const TEMPLATES = [
  {
    id: "profesional",
    name: "Profesional",
    description: "Formato corporativo con tabla de datos, contactos y firma. Sin historial de cambios.",
    preview: ["Encabezado con logo", "Tabla de datos completa", "Descripcion + Comentarios", "Evidencia fotografica", "Seguimiento", "Firma cliente/ingeniero"],
  },
  {
    id: "compacto",
    name: "Compacto",
    description: "Formato resumido sin imagenes de evidencia, ideal para impresion rapida.",
    preview: ["Encabezado con logo", "Tabla de datos completa", "Descripcion + Comentarios", "Seguimiento", "Firma cliente/ingeniero"],
  },
  {
    id: "detallado",
    name: "Detallado",
    description: "Incluye todo: evidencia, comentarios, historial completo de cambios y firma.",
    preview: ["Encabezado con logo", "Tabla de datos completa", "Descripcion + Comentarios", "Evidencia fotografica", "Seguimiento", "Historial de cambios", "Firma cliente/ingeniero"],
  },
];

export const TicketConfig = () => {
  const { token } = useAuth();
  const [supportTypes, setSupportTypes] = useState([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#6366F1");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ selected_template: "profesional", primary_color: "#1E40AF", accent_color: "#3B82F6" });
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [stRes, cfgRes] = await Promise.all([
        axios.get(`${API}/support-types`, { headers }),
        axios.get(`${API}/ticket-config`, { headers }),
      ]);
      setSupportTypes(stRes.data || []);
      setConfig(cfgRes.data || config);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleCreateType = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/support-types`, { name: newName.trim(), color: newColor }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tipo de soporte creado");
      setNewName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteType = async (id, name) => {
    if (!window.confirm(`Eliminar "${name}"?`)) return;
    try {
      await axios.delete(`${API}/support-types/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleSaveConfig = async (updates) => {
    setSavingConfig(true);
    try {
      const res = await axios.put(`${API}/ticket-config`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
      toast.success("Configuracion guardada");
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="ticket-config">
      {/* ====== SUPPORT TYPES ====== */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <Tag size={18} className="text-blue-600" />
          Tipos de Soporte
        </h3>
        <p className="text-sm text-slate-500 mb-5">Define los canales de atencion disponibles al crear un ticket.</p>

        <div className="space-y-2 mb-5">
          {supportTypes.map((st) => (
            <div key={st.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-lg" data-testid={`support-type-item-${st.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full flex-shrink-0 border border-white shadow-sm" style={{ backgroundColor: st.color || "#6366F1" }} />
                <span className="text-sm font-medium text-slate-700">{st.name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDeleteType(st.id, st.name)} className="text-red-500 hover:bg-red-50 h-7 w-7 p-0" data-testid={`delete-support-type-${st.id}`}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>

        <form onSubmit={handleCreateType} className="flex items-end gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-lg" data-testid="create-support-type-form">
          <div className="flex-1">
            <Label className="text-xs text-slate-600">Nombre</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ej: Remoto, En sitio..." className="input-sharp mt-1 h-9" data-testid="new-support-type-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Color</Label>
            <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-9 h-9 mt-1 rounded border border-slate-200 cursor-pointer" data-testid="new-support-type-color" />
          </div>
          <Button type="submit" size="sm" className="btn-primary h-9" disabled={loading || !newName.trim()} data-testid="create-support-type-btn">
            <Plus size={14} className="mr-1" /> Agregar
          </Button>
        </form>
      </div>

      {/* ====== PDF TEMPLATES ====== */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <FileText size={18} className="text-blue-600" />
          Plantilla de PDF
        </h3>
        <p className="text-sm text-slate-500 mb-5">Selecciona la plantilla que se usara al exportar tickets a PDF.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TEMPLATES.map(tmpl => {
            const isSelected = config.selected_template === tmpl.id;
            return (
              <button
                key={tmpl.id}
                type="button"
                onClick={() => handleSaveConfig({ selected_template: tmpl.id })}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? "border-blue-500 bg-blue-50/60 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`template-${tmpl.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-slate-800">{tmpl.name}</span>
                  {isSelected && (
                    <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <Check size={12} weight="bold" className="text-white" />
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">{tmpl.description}</p>
                <div className="space-y-1">
                  {tmpl.preview.map((section, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      <span className="text-[11px] text-slate-400">{section}</span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ====== COLORS ====== */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <Palette size={18} className="text-blue-600" />
          Colores del PDF
        </h3>
        <p className="text-sm text-slate-500 mb-5">Personaliza los colores usados en los encabezados y tablas del PDF.</p>

        <div className="flex items-end gap-6">
          <div>
            <Label className="text-xs text-slate-600">Color principal</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={config.primary_color || "#1E40AF"}
                onChange={(e) => setConfig(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                data-testid="primary-color-picker"
              />
              <span className="text-xs text-slate-500 font-mono">{config.primary_color}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Color de acento</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={config.accent_color || "#3B82F6"}
                onChange={(e) => setConfig(prev => ({ ...prev, accent_color: e.target.value }))}
                className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                data-testid="accent-color-picker"
              />
              <span className="text-xs text-slate-500 font-mono">{config.accent_color}</span>
            </div>
          </div>
          <Button
            onClick={() => handleSaveConfig({ primary_color: config.primary_color, accent_color: config.accent_color })}
            className="btn-primary h-10"
            disabled={savingConfig}
            data-testid="save-colors-btn"
          >
            Guardar Colores
          </Button>

          {/* Preview */}
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-slate-400">Vista previa:</div>
            <div className="flex gap-1 h-8">
              <div className="w-16 rounded-l" style={{ backgroundColor: config.primary_color }} />
              <div className="w-16 rounded-r" style={{ backgroundColor: config.accent_color }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
